const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_IDEMPOTENT_RETRIES = 2;
const DEFAULT_RETRY_DELAY_MS = 250;
const DEFAULT_MAX_RETRY_DELAY_MS = 1_000;

export class ApiClientError extends Error {
  readonly details?: string;
  readonly isTransient: boolean;
  readonly status?: number;

  constructor(
    message: string,
    options: { details?: string; isTransient?: boolean; status?: number } = {},
  ) {
    super(message);
    this.name = "ApiClientError";
    this.details = options.details;
    this.isTransient = options.isTransient ?? false;
    this.status = options.status;
  }
}

type FetchJsonOptions = {
  fallbackErrorMessage: string;
  retry?:
    | false
    | {
        delayMs?: number;
        maxDelayMs?: number;
        retries?: number;
      };
  timeoutMs?: number;
};

function isAbortError(error: unknown): boolean {
  return (
    typeof DOMException !== "undefined" &&
    error instanceof DOMException &&
    error.name === "AbortError"
  );
}

function isTransientStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

function getRetryConfig(init: RequestInit, options: FetchJsonOptions) {
  if (options.retry === false) {
    return {
      delayMs: DEFAULT_RETRY_DELAY_MS,
      maxDelayMs: DEFAULT_MAX_RETRY_DELAY_MS,
      retries: 0,
    };
  }

  const method = (init.method || "GET").toUpperCase();
  const defaultRetries =
    method === "GET" || method === "HEAD" ? DEFAULT_IDEMPOTENT_RETRIES : 0;
  const retryOptions = options.retry || {};

  return {
    delayMs: retryOptions.delayMs ?? DEFAULT_RETRY_DELAY_MS,
    maxDelayMs: retryOptions.maxDelayMs ?? DEFAULT_MAX_RETRY_DELAY_MS,
    retries: Math.max(0, Math.floor(retryOptions.retries ?? defaultRetries)),
  };
}

function getRetryDelayMs(
  attemptIndex: number,
  delayMs: number,
  maxDelayMs: number,
): number {
  return Math.min(delayMs * 2 ** attemptIndex, maxDelayMs);
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function readApiError(
  response: Response,
  fallbackErrorMessage: string,
): Promise<ApiClientError> {
  try {
    const errorData = await response.json();
    const message =
      typeof errorData?.error === "string"
        ? errorData.error
        : fallbackErrorMessage;
    const details =
      typeof errorData?.details === "string" ? errorData.details : undefined;

    return new ApiClientError(message, {
      details,
      isTransient: isTransientStatus(response.status),
      status: response.status,
    });
  } catch {
    return new ApiClientError(fallbackErrorMessage, {
      isTransient: isTransientStatus(response.status),
      status: response.status,
    });
  }
}

function normalizeFetchError(
  error: unknown,
  fallbackErrorMessage: string,
): Error {
  if (error instanceof ApiClientError) return error;

  if (isAbortError(error)) {
    return new ApiClientError("The request timed out. Please try again.", {
      isTransient: true,
    });
  }

  if (error instanceof TypeError) {
    return new ApiClientError(fallbackErrorMessage, { isTransient: true });
  }

  return error instanceof Error ? error : new Error(String(error));
}

export function isTransientApiError(error: unknown): boolean {
  return error instanceof ApiClientError && error.isTransient;
}

export function isUnauthorizedApiError(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 401;
}

export function getApiErrorDescription(error: unknown): string {
  if (error instanceof ApiClientError) {
    return error.details && error.details !== error.message
      ? error.details
      : error.message;
  }

  if (isAbortError(error)) return "The request timed out. Please try again.";

  return error instanceof Error ? error.message : "Please try again.";
}

export async function fetchJson<T>(
  input: RequestInfo | URL,
  init: RequestInit,
  options: FetchJsonOptions,
): Promise<T> {
  const { delayMs, maxDelayMs, retries } = getRetryConfig(init, options);

  for (let attemptIndex = 0; attemptIndex <= retries; attemptIndex += 1) {
    try {
      return await fetchJsonOnce(input, init, options);
    } catch (error) {
      const normalizedError = normalizeFetchError(
        error,
        options.fallbackErrorMessage,
      );
      const shouldRetry =
        attemptIndex < retries &&
        isTransientApiError(normalizedError) &&
        !init.signal?.aborted;

      if (!shouldRetry) {
        throw normalizedError;
      }

      await wait(getRetryDelayMs(attemptIndex, delayMs, maxDelayMs));
    }
  }

  throw new ApiClientError(options.fallbackErrorMessage, {
    isTransient: true,
  });
}

async function fetchJsonOnce<T>(
  input: RequestInfo | URL,
  init: RequestInit,
  options: FetchJsonOptions,
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  );
  const abortListener = () => controller.abort();

  try {
    if (init.signal) {
      if (init.signal.aborted) {
        controller.abort();
      } else {
        init.signal.addEventListener("abort", abortListener);
      }
    }

    const requestInit = { ...init };
    Object.defineProperty(requestInit, "signal", {
      configurable: true,
      enumerable: false,
      value: controller.signal,
      writable: true,
    });
    const response = await fetch(input, requestInit);

    if (!response.ok) {
      throw await readApiError(response, options.fallbackErrorMessage);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  } catch (error) {
    throw normalizeFetchError(error, options.fallbackErrorMessage);
  } finally {
    init.signal?.removeEventListener("abort", abortListener);
    clearTimeout(timeoutId);
  }
}
